import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import "./style.scss";
import { ExploreContext } from "context/ExploreContext";
import GridCards from "../GridCards";
import ListCards from "../ListCards";
import useMediaQuery from "hooks/useMediaQuery";
import { useLocation, useNavigate } from "react-router-dom";

function CardLists({
  data = [],
  isListing,
  paginationDetails,
  setPagination,
  isLoading,
  selectedList,
}) {
  const { cardLayout, setCardLayout, tabActive, filterData } =
    useContext(ExploreContext);
  const isMobile = useMediaQuery("(max-width: 820px)");
  const [allData, setAllData] = useState([]);
  const scrollPositionRef = useRef(0);
  const observerRef = useRef(null);
  const loadTriggerRef = useRef(null);
  const delayTimerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const ActionChange = (key, id, action = "replace") => {
    setAllData((prevData) => {
      if (action === "replace") {
        return prevData.map((item) =>
          item.id === id ? { ...item, [key]: !item[key] } : item,
        );
      } else if (action === "remove") {
        return prevData.filter((item) => item.id !== id);
      }
      return prevData;
    });
  };

  const handleProfileClick = (id) => {
    const currentScroll = window.scrollY;
    const scrollKey = `scroll-${location.pathname}-tab-${tabActive}`;

    sessionStorage.setItem(scrollKey, String(currentScroll));
    sessionStorage.setItem("profileListPath", location.pathname);
    sessionStorage.setItem("exploreActiveTab", tabActive);

    navigate(`/person-profile/${id}`, {
      state: {
        fromProfileDetail: true,
        returnPath: location.pathname,
        savedScroll: currentScroll,
      },
    });
  };

  useEffect(() => {
    if (isMobile) {
      setCardLayout("grid");
    }
  }, [isMobile]);

  const lastFilterRef = useRef(filterData);
  const lastPathRef = useRef(location.pathname);
  const lastTabRef = useRef(tabActive);

  useEffect(() => {
    if (JSON.stringify(filterData) !== JSON.stringify(lastFilterRef.current)) {
      setPagination(1);
      setAllData([]);
      lastFilterRef.current = filterData;
    }
  }, [filterData]);

  useEffect(() => {
    if (!data?.length) return;

    const filterChanged =
      JSON.stringify(filterData) !== JSON.stringify(lastFilterRef.current);
    const pathnameChanged = location.pathname !== lastPathRef.current;
    const tabChanged = tabActive !== lastTabRef.current;

    if (filterChanged || pathnameChanged || tabChanged) {
      setAllData([...data]);
    } else if (paginationDetails?.page > 1) {
      setAllData((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const newItems = data.filter((item) => !existingIds.has(item.id));
        return [...prev, ...newItems];
      });
    } else {
      setAllData([...data]);
    }

    lastFilterRef.current = filterData;
    lastPathRef.current = location.pathname;
    lastTabRef.current = tabActive;
  }, [data, paginationDetails?.page, filterData, tabActive, location.pathname]);

  useEffect(() => {
    const isFromProfileDetail = location.state?.fromProfileDetail === true;
    const filterChanged =
      JSON.stringify(filterData) !== JSON.stringify(lastFilterRef.current);

    if (filterChanged && !isFromProfileDetail) {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [filterData, location.state?.fromProfileDetail]);

  const transformedData = allData?.map((item) => ({
    aec_id: item?.aec_id,
    id: item.id,
    profileImage: item.profile_image,
    imageCount: item.image_count,
    profileName: item.name,
    profileAge: item.age,
    profileHeight: item.height,
    profileOcc: item.profession,
    verificationType: item.verification_type,
    isShow: !item.allow_image_view,
    isLike: item.has_expressed_interest,
    isFav: item.has_favorited,
    isPremium: item.is_premium_member,
    isSuggest: false,
    profileId: item.aec_id,
    lastSeen: item.last_seen,
    religion: item.religion,
    maritalStatus: item.marital_status,
    livesIn: item.lives_in,
    primaryLanguage: item.primary_language,
    createdBy: item.created_by,
    image_request: item?.image_requested,
    isPrivate: item?.is_private,
    shareUrl: item?.share_url,
  }));

  const firstHalf = transformedData.slice(0, 8);
  const secondHalf = transformedData.slice(8);

  const totalPages = Math.ceil(
    paginationDetails?.count / paginationDetails?.limit,
  );
  const isMoreData = paginationDetails?.page < totalPages;

  useLayoutEffect(() => {
    if (paginationDetails?.page > 1) {
      window.scrollTo({
        top: scrollPositionRef.current,
        behavior: "auto",
      });
    }
  }, [allData]);

  useEffect(() => {
    if (!isMoreData || isLoading) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver((entries) => {
      const target = entries[0];
      if (target.isIntersecting && !isLoading) {
        if (!delayTimerRef.current) {
          delayTimerRef.current = setTimeout(() => {
            scrollPositionRef.current = window.scrollY;
            setPagination((prev) => prev + 1);
            delayTimerRef.current = null;
          }, 600);
        }
      }
    });

    if (loadTriggerRef.current) {
      observerRef.current.observe(loadTriggerRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
    };
  }, [isMoreData, isLoading, setPagination]);

  return (
    <div className="explore-tab-content">
      <div className="flex flex-col gap-6">
        {cardLayout === "grid" ? (
          <GridCards
            ActionChange={ActionChange}
            firstHalf={firstHalf}
            secondHalf={secondHalf}
            isListing={isListing}
            tabActive={tabActive}
            onProfileClick={handleProfileClick}
            isLoading={isLoading}
            selectedList={selectedList}
          />
        ) : (
          <ListCards
            ActionChange={ActionChange}
            firstHalf={firstHalf}
            secondHalf={secondHalf}
            isListing={isListing}
            tabActive={tabActive}
            onProfileClick={handleProfileClick}
            isLoading={isLoading}
            selectedList={selectedList}
          />
        )}
      </div>
      {isMoreData && <div ref={loadTriggerRef}></div>}
    </div>
  );
}

export default CardLists;
